/**
 * AuditTimeline - side-rail card that renders the last N ticket_events
 * for one ticket (SPEC §9 "Audit trail timeline - last 20 events").
 *
 * Each row condenses one event_type into a one-line summary:
 *   - actor name (or actor_kind fallback when no user is attached)
 *   - verb phrase ("changed status from open to pending", "added a CC",
 *     "marked spam", etc.) inferred from event_type plus from/to/metadata
 *   - relative timestamp
 *
 * The full audit lives at /admin/audit?ticket_id=...; this card is a
 * read-only digest for agent triage. The "View full audit" link routes
 * there with the ticket id pre-filled - but only for system_admins,
 * since /admin is gated.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { Spinner } from "@/components/ui";
import { useAuthStore } from "@/stores/auth";
import { useTicketEvents } from "@/hooks/useTicketEvents";
import { useCustomerTicketEvents } from "@/hooks/useCustomerTicketEvents";
import type {
  AgentTicketEventResponse,
  AuditActorKind,
  CustomerTicketEventResponse,
} from "@/types/api";

/** Anchor id used by the agent detail page to scroll to a comment when
 * the timeline row is clicked. Exported so the page can put the same
 * id on its comment bubbles. */
export function commentAnchorId(commentId: string): string {
  return `agent-comment-${commentId}`;
}

/**
 * Scroll the main thread to one comment bubble and flash it.
 *
 * Returns false when the bubble is not in the DOM at all, which since
 * 2026-08-24 is a real and expected case: the detail page only renders
 * the newest few messages until the agent loads the rest of the history
 * (plans/2026-08-24-ticket-detail-comment-windowing.md). The caller
 * decides what to do about that; this function itself never throws and
 * never guesses.
 *
 * Exported because the page needs the identical behavior when it
 * re-attempts the jump after the history has loaded - two copies of the
 * expand + scroll + highlight sequence would drift.
 */
export function focusCommentAnchor(commentId: string): boolean {
  const el = document.getElementById(commentAnchorId(commentId));
  if (!el) return false;
  // API-authored internal notes render collapsed, so jumping to one
  // would otherwise land the agent on a header with nothing under
  // it. The bubble owns its open state in React; click its toggle
  // rather than reaching in and changing classes from here.
  el.querySelector<HTMLButtonElement>(
    '[data-testid="bubble-toggle"][aria-expanded="false"]',
  )?.click();
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // Brief highlight ring so the agent's eye lands on it. Use a data
  // attribute + CSS so we don't need refs or class juggling here.
  el.setAttribute("data-recently-focused", "true");
  window.setTimeout(() => {
    el.removeAttribute("data-recently-focused");
  }, 1800);
  return true;
}

/** Pluck the comment id out of a comment_added / comment_edited /
 * comment_deleted event. The id lives in different slots depending on
 * how the comment-service emitted the event over time; check all
 * plausible keys before giving up. Returning null means "this event
 * isn't a comment - no scroll target". */
function commentIdFromEvent(
  event: AgentTicketEventResponse,
): string | null {
  if (
    event.event_type !== "comment_added" &&
    event.event_type !== "comment_edited" &&
    event.event_type !== "comment_deleted"
  ) {
    return null;
  }
  const to = (event.to_value ?? {}) as Record<string, unknown>;
  const meta = (event.event_metadata ?? {}) as Record<string, unknown>;
  const candidate =
    (typeof to.comment_id === "string" && to.comment_id) ||
    (typeof to.id === "string" && to.id) ||
    (typeof meta.comment_id === "string" && meta.comment_id) ||
    null;
  return candidate || null;
}

export interface AuditTimelineProps {
  ticketId: string;
  ticketNumber: number;
  /** Soft cap on rows fetched from the endpoint. Defaults to 20 to match
   * the SPEC §9 wording on the agent detail page. */
  limit?: number;
  /** Which surface this rail is mounted on. ``agent`` (default) hits
   * GET /agent/tickets/{n}/events and renders the Show-All toggle +
   * the View-Full-Audit admin link. ``customer`` hits GET
   * /tickets/{n}/events (server-scrubbed, no operational events, no
   * private-comment leakage) and renders neither toggle nor admin
   * link - the customer surface is intentionally read-only and
   * major-only. */
  mode?: "agent" | "customer";
  /** Called when a clicked comment row has no bubble in the DOM, i.e.
   * the comment sits in history the page has not loaded yet. The page
   * is expected to load it and then re-run ``focusCommentAnchor``.
   * Omitted on surfaces that render every comment. */
  onRequestCommentReveal?: (commentId: string) => void;
}

// --- formatting helpers ----------------------------------------------

function actorLabel(event: AgentTicketEventResponse): string {
  if (event.actor_kind === "user") {
    return event.actor_name?.trim() || event.actor_email || "An agent";
  }
  if (event.actor_kind === "api_key") {
    // Backend joins api_keys.name into actor_name when actor_kind is
    // api_key, so the timeline can render "claude-support replied by
    // email" instead of the generic kind fallback. Only fall back to
    // "An API key" when the key was revoked / deleted and the name
    // join came back null.
    return event.actor_name?.trim() || "An API key";
  }
  const kindLabels: Record<AuditActorKind, string> = {
    user: "An agent",
    api_key: "An API key",
    email_inbound: "Inbound email",
    system: "System",
    import: "Importer",
  };
  return kindLabels[event.actor_kind] ?? "Someone";
}

function asString(v: unknown): string | null {
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return null;
}

/**
 * Turn one event into a human sentence fragment that reads naturally
 * after the actor name ("Alice <PHRASE>"). The from/to/metadata shapes
 * are documented in SPEC §6.
 */
function eventPhrase(event: AgentTicketEventResponse): string {
  const from = (event.from_value ?? {}) as Record<string, unknown>;
  const to = (event.to_value ?? {}) as Record<string, unknown>;
  const meta = (event.event_metadata ?? {}) as Record<string, unknown>;

  switch (event.event_type) {
    case "ticket_created":
      return "created the ticket";
    case "status_changed": {
      const a = asString(from.status);
      const b = asString(to.status);
      if (a && b) return `changed status from ${a} to ${b}`;
      if (b) return `changed status to ${b}`;
      return "changed the status";
    }
    case "priority_changed": {
      const a = asString(from.priority);
      const b = asString(to.priority);
      if (a && b) return `changed priority from ${a} to ${b}`;
      if (b) return `set priority to ${b}`;
      return "changed the priority";
    }
    case "type_changed": {
      const a = asString(from.type);
      const b = asString(to.type);
      if (a && b) return `changed type from ${a} to ${b}`;
      if (b) return `set type to ${b}`;
      return "changed the type";
    }
    case "assignee_changed": {
      const name = asString(to.name);
      const id = asString(to.assignee_user_id);
      // Self-assignment (agent picks up an unassigned ticket by
      // responding to it - the auto-assign rule from 2026-05-19)
      // reads cleaner as "picked up the ticket" than the awkward
      // "Gregory Silvano assigned the ticket to Gregory Silvano".
      if (id && event.actor_user_id && id === event.actor_user_id) {
        return "picked up the ticket";
      }
      if (name) return `assigned the ticket to ${name}`;
      if (id) return "assigned the ticket";
      return "unassigned the ticket";
    }
    case "requester_changed": {
      const name = asString(to.name) ?? asString(to.email);
      if (name) return `changed the requester to ${name}`;
      return "changed the requester";
    }
    case "brand_changed":
      return "changed the brand";
    case "subject_changed":
      return "edited the subject";
    case "current_status_updated":
      // The event carries plain-text previews rather than the bodies
      // (see ticket_service._current_status_preview); the current
      // value itself is pinned at the top of the page, so the row is
      // about WHEN it moved, not what it says.
      return asString(to.current_status_preview)
        ? "updated the Current Status"
        : "cleared the Current Status";
    case "tag_added": {
      const tag = asString(to.tag);
      return tag ? `added tag ${tag}` : "added a tag";
    }
    case "tag_removed": {
      const tag = asString(from.tag);
      return tag ? `removed tag ${tag}` : "removed a tag";
    }
    case "ticket_merged_from": {
      const code = asString(meta.source_public_code);
      return code
        ? `merged ticket [#${code}] into this one`
        : "merged another ticket into this one";
    }
    case "ticket_merged_into": {
      const code = asString(meta.target_public_code);
      return code
        ? `merged this ticket into [#${code}]`
        : "merged this ticket into another";
    }
    case "ticket_split_out": {
      const code = asString(meta.new_public_code);
      return code
        ? `split messages out into new ticket [#${code}]`
        : "split messages out into a new ticket";
    }
    case "ticket_split_from": {
      const code = asString(meta.source_public_code);
      return code
        ? `created this ticket by splitting it out of [#${code}]`
        : "created this ticket by splitting it out of another";
    }
    case "comments_moved_out": {
      const code = asString(meta.target_public_code);
      return code ? `moved messages to [#${code}]` : "moved messages to another ticket";
    }
    case "comments_moved_in": {
      const code = asString(meta.source_public_code);
      return code ? `moved messages here from [#${code}]` : "moved messages here from another ticket";
    }
    case "email_inbound_new_subject": {
      const subject = asString(meta.subject);
      return subject
        ? `received a reply with a new subject: ${subject}`
        : "received a reply with a new subject";
    }
    case "cc_added": {
      const email = asString(to.email) ?? asString(meta.email);
      return email ? `added ${email} as CC` : "added a CC";
    }
    case "cc_removed": {
      const email = asString(from.email) ?? asString(meta.email);
      return email ? `removed ${email} from CC` : "removed a CC";
    }
    case "bcc_added": {
      const email = asString(to.email) ?? asString(meta.email);
      return email ? `added ${email} as BCC` : "added a BCC";
    }
    case "bcc_removed": {
      const email = asString(from.email) ?? asString(meta.email);
      return email ? `removed ${email} from BCC` : "removed a BCC";
    }
    case "comment_added": {
      const isPrivate = to.is_private === true;
      const viaEmail = to.sent_via_email === true;
      if (isPrivate) return "added an internal note";
      return viaEmail ? "replied by email" : "added a public comment";
    }
    case "comment_edited": {
      // Same private/public split as comment_added so the agent can
      // tell from the timeline whether the edited row was a note or a
      // reply without clicking through.
      const isPrivate = to.is_private === true;
      return isPrivate ? "edited an internal note" : "edited a reply";
    }
    case "comment_deleted": {
      const isPrivate = from.is_private === true || to.is_private === true;
      return isPrivate ? "deleted an internal note" : "deleted a reply";
    }
    case "comment_sent":
      // Emitted when an agent promotes an api_key+private draft to a
      // public reply via the Send button on the draft bubble. Pairs
      // with a subsequent email_sent row from the notification service.
      return "sent the AI draft as a reply";
    case "attachment_added": {
      const name = asString(to.filename) ?? asString(meta.filename);
      return name ? `attached ${name}` : "added an attachment";
    }
    case "marked_spam":
      return "marked the ticket as spam";
    case "unmarked_spam":
      return "removed the spam flag";
    case "sender_blocked": {
      const target = asString(to.email_or_domain) ?? asString(meta.email_or_domain);
      return target ? `blocked sender ${target}` : "blocked the sender";
    }
    case "email_sent": {
      const to_addr = asString(to.to) ?? asString(meta.to);
      return to_addr ? `sent email to ${to_addr}` : "sent an email";
    }
    case "email_inbound": {
      const from_addr = asString(meta.from) ?? asString(from.from);
      return from_addr ? `received email from ${from_addr}` : "received an inbound email";
    }
    case "webhook_delivered":
      return "delivered a webhook";
    case "webhook_failed":
      return "failed to deliver a webhook";
    case "ticket_deleted":
      return "deleted the ticket";
    case "ticket_undeleted":
      return "restored the ticket";
    case "imported_from_freshdesk":
      return "imported the ticket from Freshdesk";
    case "imported_from_gemini":
      return "imported the ticket from Gemini";
    case "ticket_created_via_agent_forward": {
      // The forwarding agent is the row's actor; we still let the
      // metadata's forwarding_agent_email win as a label when the actor
      // join did not produce a name (e.g. agent user was renamed).
      const agentLabel =
        event.actor_name?.trim() ||
        asString(meta.forwarding_agent_email) ||
        event.actor_email ||
        "(agent)";
      return `Forwarded by ${agentLabel}`;
    }
    case "agent_forward_unparsed_fallback": {
      const agentLabel =
        event.actor_name?.trim() ||
        asString(meta.forwarding_agent_email) ||
        event.actor_email ||
        "(agent)";
      return `Agent ${agentLabel} forwarded an email but the customer could not be auto-parsed; ticket attributed to the agent`;
    }
    default:
      // Fall back to the raw event_type so a new vocabulary term is
      // visible to the agent and shows up in screenshots; the renderer
      // never throws on an unknown type.
      return `recorded a ${event.event_type.replace(/_/g, " ")} event`;
  }
}

/**
 * Optional muted trailing detail for a row (rendered after the main
 * phrase, in a smaller subdued span). Most events return null. The
 * agent-forward event uses this to surface the parsed customer email
 * inline so triage agents can see "who is this really from" at a glance.
 */
function eventDetail(event: AgentTicketEventResponse): string | null {
  const meta = (event.event_metadata ?? {}) as Record<string, unknown>;
  if (event.event_type === "ticket_created_via_agent_forward") {
    const customer = asString(meta.parsed_customer_email);
    return customer ? `for ${customer}` : null;
  }
  return null;
}

/**
 * Optional native-title (hover tooltip) string for the row. Returns
 * null when no tooltip is appropriate. Used by the agent-forward event
 * to expose the body_split_into_first_comment boolean and the parsed
 * customer email without crowding the visible row.
 */
function eventRowTitle(event: AgentTicketEventResponse): string | null {
  const meta = (event.event_metadata ?? {}) as Record<string, unknown>;
  if (event.event_type === "ticket_created_via_agent_forward") {
    const customer = asString(meta.parsed_customer_email) ?? "(unknown)";
    const split = meta.body_split_into_first_comment === true;
    const splitText = split
      ? "agent prefix saved as first comment"
      : "pure forward (no agent prefix)";
    return `Forwarded by agent for ${customer}. ${splitText}.`;
  }
  return null;
}

/**
 * Optional override for the row's main-line className. Returns null
 * when the default neutral-700 styling applies. Used by the unparsed
 * fallback event to render as a muted, italic "warning but not error"
 * line so the agent can see the auto-parse failed without it looking
 * like a real error.
 */
function eventToneClass(event: AgentTicketEventResponse): string | null {
  if (event.event_type === "agent_forward_unparsed_fallback") {
    return "text-xs italic leading-snug text-neutral-500";
  }
  return null;
}

/**
 * Whether to suppress the leading actor-name span for a given event.
 * Most rows read "<Actor> <phrase>" and need the prefix. The two
 * agent-forward event types compose the agent label into the phrase
 * directly ("Forwarded by Greg Silvano"), so the prefix would be
 * redundant ("Greg Silvano Forwarded by Greg Silvano").
 */
function eventOmitsActorPrefix(event: AgentTicketEventResponse): boolean {
  return (
    event.event_type === "ticket_created_via_agent_forward" ||
    event.event_type === "agent_forward_unparsed_fallback"
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

// --- component --------------------------------------------------------

export function AuditTimeline({
  ticketId,
  ticketNumber,
  limit = 20,
  mode = "agent",
  onRequestCommentReveal,
}: AuditTimelineProps): JSX.Element {
  // ``showAll`` toggles between the default "major events only" view
  // (SPEC §9) and the full firehose. Local state, not persisted - it
  // resets when the agent navigates away. Customer mode pins this to
  // major-only and hides the toggle (the customer endpoint enforces
  // the allowlist server-side; flipping the bit here would still get
  // the major-only response).
  const [showAll, setShowAll] = useState(false);
  // The two hooks both expose UseQueryResult; React only sees one of
  // them as "active" per render thanks to the early-return at the
  // bottom of the branch, but to stay within the Rules of Hooks we
  // call both. The disabled hook short-circuits inside useQuery so
  // there's no real network cost.
  const agentEvents = useTicketEvents(ticketNumber, {
    limit,
    majorOnly: !showAll,
    enabled: mode === "agent",
  });
  const customerEvents = useCustomerTicketEvents(ticketNumber, {
    limit,
    enabled: mode === "customer",
  });
  const customerEventsAsAgentShape: AgentTicketEventResponse[] | undefined =
    customerEvents.data?.map(
      (e: CustomerTicketEventResponse): AgentTicketEventResponse => ({
        id: e.id,
        event_type: e.event_type,
        actor_kind: "user" as AuditActorKind,
        actor_user_id: null,
        actor_name: e.actor_name,
        actor_email: null,
        from_value: null,
        to_value: e.to_value as Record<string, unknown> | null,
        event_metadata: null,
        created_at: e.created_at,
      }),
    );
  const data = mode === "customer" ? customerEventsAsAgentShape : agentEvents.data;
  const isLoading =
    mode === "customer" ? customerEvents.isLoading : agentEvents.isLoading;
  const isError =
    mode === "customer" ? customerEvents.isError : agentEvents.isError;
  const error =
    mode === "customer" ? customerEvents.error : agentEvents.error;
  const isSystemAdmin = useAuthStore((s) => s.user?.isSystemAdmin ?? false);

  // Clicking a comment_* row scrolls the main thread to that comment.
  // The agent detail page mounts each bubble with id=commentAnchorId(c.id)
  // so getElementById is the cheapest stable lookup.
  //
  // A miss means the bubble is not rendered. Since the conversation
  // window landed that is normal rather than exceptional - the comment
  // is in the not-yet-loaded history - so hand it to the host page,
  // which loads the history and re-runs the jump once the bubble
  // exists. Without a host callback (customer mode) the click stays the
  // silent no-op it has always been.
  function scrollToComment(commentId: string): void {
    if (focusCommentAnchor(commentId)) return;
    onRequestCommentReveal?.(commentId);
  }

  return (
    <div data-testid="audit-timeline">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-neutral-700">
          Audit timeline
        </h2>
        {mode === "agent" && (
          <div className="flex items-baseline gap-3">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-xs font-medium text-primary-700 hover:text-primary-800"
              aria-pressed={showAll}
              data-testid="audit-timeline-toggle"
            >
              {showAll ? "Show major events only" : "Show all events"}
            </button>
            {isSystemAdmin && (
              <Link
                to={`/admin/audit?ticket_id=${ticketId}`}
                className="text-xs font-medium text-primary-700 hover:text-primary-800"
              >
                View full audit
              </Link>
            )}
          </div>
        )}
      </div>

      {isLoading && (
        <div className="mt-3 flex items-center justify-center py-4">
          <Spinner />
        </div>
      )}

      {isError && (
        <p className="mt-3 text-xs text-red-700">
          {error instanceof Error
            ? error.message
            : "Failed to load the audit timeline."}
        </p>
      )}

      {!isLoading && !isError && data && data.length === 0 && (
        <p className="mt-3 text-xs text-neutral-500">
          No audit events recorded yet.
        </p>
      )}

      {!isLoading && !isError && data && data.length > 0 && (
        <ol className="mt-3 space-y-3 border-l border-neutral-200 pl-4">
          {data.map((event) => {
            const tone = eventToneClass(event);
            const detail = eventDetail(event);
            const rowTitle = eventRowTitle(event);
            const omitActor = eventOmitsActorPrefix(event);
            const lineClass = tone ?? "text-xs leading-snug text-neutral-700";
            const commentId = commentIdFromEvent(event);
            const isClickable = commentId !== null;
            return (
              <li
                key={event.id}
                data-event-id={event.id}
                data-event-type={event.event_type}
                className={`relative ${
                  isClickable
                    ? "cursor-pointer rounded-md -mx-1 px-1 hover:bg-neutral-50"
                    : ""
                }`}
                {...(isClickable
                  ? {
                      role: "button",
                      tabIndex: 0,
                      onClick: () => scrollToComment(commentId!),
                      onKeyDown: (e: React.KeyboardEvent<HTMLLIElement>) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          scrollToComment(commentId!);
                        }
                      },
                    }
                  : {})}
              >
                {/* Bullet on the rail. -left-[1.05rem] aligns the dot
                    with the ol's border-l (4px padding + 1rem ≈ 1.05rem
                    back). */}
                <span
                  aria-hidden="true"
                  className="absolute -left-[1.18rem] top-1.5 h-2 w-2 rounded-full bg-primary-500 ring-2 ring-white"
                />
                <p
                  className={lineClass}
                  {...(rowTitle ? { title: rowTitle } : {})}
                >
                  {!omitActor && (
                    <>
                      <span className="font-medium text-neutral-900">
                        {actorLabel(event)}
                      </span>{" "}
                    </>
                  )}
                  {eventPhrase(event)}
                  {detail && (
                    <span className="text-xs text-neutral-500">
                      {" "}
                      {detail}
                    </span>
                  )}
                </p>
                <p
                  className="mt-0.5 text-[0.6875rem] text-neutral-400"
                  title={new Date(event.created_at).toLocaleString()}
                >
                  {formatRelative(event.created_at)}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export default AuditTimeline;
